import { LearningCoreRestCRUDService } from '../common/LearningCoreRestCRUDService';
import { IUnitModel } from '../unit/interface/IUnitModel';
import { IContentTransferService, IImportBatchOptions, IImportResult } from './IContentTransferService';

const DEFAULT_IMPORT_BATCH_MAX_BYTES = 45 * 1024 * 1024;

type IImportBatchChunkError = Error & {
  processedCount: number;
  chunkSize: number;
  partialResults: IImportResult[];
  originalError: unknown;
};

export class RestContentTransferService
  extends LearningCoreRestCRUDService<IUnitModel>
  implements IContentTransferService
{
  private addInfoForAuth(additionalHeaders = {}): RequestInit {
    const csrf = this.getCurrentCsrfCookie();

    return {
      headers: {
        ...additionalHeaders,
        ...(csrf ? { 'x-csrf-token': csrf } : {}),
      },
      credentials: 'include',
    };
  }

  private async postMultipart<T>(url: string, form: FormData, uploadErrorText: string): Promise<T> {
    const response = await fetch(url, {
      ...this.addInfoForAuth(),
      method: 'POST',
      body: form,
    });

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      if (result) {
        throw result;
      }

      throw new Error(`${uploadErrorText}: ${response.status} ${response.statusText}`);
    }

    return result as T;
  }

  private splitFilesByMaxBytes(files: File[], maxBatchBytes: number): File[][] {
    const chunks: File[][] = [];
    let currentChunk: File[] = [];
    let currentChunkSize = 0;

    files.forEach((file) => {
      const nextChunkSize = currentChunkSize + file.size;

      if (currentChunk.length > 0 && nextChunkSize > maxBatchBytes) {
        chunks.push(currentChunk);
        currentChunk = [];
        currentChunkSize = 0;
      }

      currentChunk.push(file);
      currentChunkSize += file.size;
    });

    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }

    return chunks;
  }

  private async importBatchRequest(files: File[], password: string, withDependencies: boolean): Promise<IImportResult[]> {
    const form = new FormData();
    files.forEach((file) => form.append('files', file));
    form.append('password', password);
    form.append('withDependencies', withDependencies ? 'true' : 'false');

    return this.postMultipart<IImportResult[]>(`${this.getEndpoint()}/import-batch`, form, 'Batch upload failed').catch(
      this.handleError,
    );
  }

  private createChunkError(
    error: unknown,
    processedCount: number,
    chunkSize: number,
    partialResults: IImportResult[],
  ): IImportBatchChunkError {
    const defaultMessage = 'Batch upload failed';
    const message = error instanceof Error ? error.message : defaultMessage;
    const chunkError = new Error(message) as IImportBatchChunkError & Record<string, unknown>;

    chunkError.processedCount = processedCount;
    chunkError.chunkSize = chunkSize;
    chunkError.partialResults = partialResults;
    chunkError.originalError = error;

    if (error && typeof error === 'object') {
      if ('messageKey' in error) {
        chunkError.messageKey = (error as Record<string, unknown>).messageKey;
      }
      if ('errorName' in error) {
        chunkError.errorName = (error as Record<string, unknown>).errorName;
      }
    }

    return chunkError;
  }

  getEndpoint(): string {
    const url = this.learningConfig.url.api.learning;
    return `${url}/content-import`;
  }

  async getUnitExport(unitId: string, password: string) {
    return await this.request()
    .url(`${this.getEndpoint()}/backup`)
    .errorType('json')
    .post({ unitId, password })
    .error(400, (err: any) => {
      const constraints =
        err?.json?.data?.[0]?.constraints ??
        err?.json?.[0]?.constraints ??
        null;

      const messages = constraints ? Object.values(constraints) : ['Bad Request'];

      const e: any = new Error(messages.join('; '));
      e.name = 'ValidationError';
      e.messages = messages;
      e.payload = err?.json;
      throw e;
    })
    .res(r => r.blob()); 
  }

  async importUnit(file: File, password: string, withDependencies: boolean) {
    const form = new FormData();
    form.append('file', file);
    form.append('password', password);
    form.append('withDependencies', withDependencies ? 'true' : 'false');

    return this.postMultipart<IImportResult>(`${this.getEndpoint()}/import`, form, 'Upload failed').catch(
      this.handleError,
    );
  }
  async importBatch(files: File[], password: string, withDependencies: boolean) {
    return this.importBatchChunked(files, password, withDependencies);
  }

  async importBatchChunked(
    files: File[],
    password: string,
    withDependencies: boolean,
    options?: IImportBatchOptions,
  ): Promise<IImportResult[]> {
    const maxBatchBytes = options?.maxBatchBytes ?? DEFAULT_IMPORT_BATCH_MAX_BYTES;
    const chunks = this.splitFilesByMaxBytes(files, maxBatchBytes);
    const result: IImportResult[] = [];
    let processedCount = 0;

    for (const chunk of chunks) {
      try {
        const chunkResult = await this.importBatchRequest(chunk, password, withDependencies);
        result.push(...chunkResult);
        processedCount += chunk.length;
        options?.onProgress?.({ current: processedCount, total: files.length });
      } catch (error) {
        throw this.createChunkError(error, processedCount, chunk.length, [...result]);
      }
    }

    return result;
  }
}
